import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import { Send, Sparkles, ArrowLeft, Bot, User as UserIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const STARTER_QUESTIONS = [
  'Which ITR form should I file?',
  'Old vs New tax regime — which is better for me?',
  'How much can I save under 80C?',
  'What documents do I need for ITR filing?',
  'I have salary + capital gains. Which ITR?',
  'When is the ITR filing deadline?',
];

const sessionKey = 'arthik_tax_chat_sid';

const TaxAssistantPage = () => {
  const navigate = useNavigate();
  const [sid] = useState(() => {
    const existing = localStorage.getItem(sessionKey);
    if (existing) return existing;
    const fresh = `tax_${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(sessionKey, fresh);
    return fresh;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/tax-chat/history/${sid}`)
      .then((r) => setMessages(r.data || []))
      .catch(() => {});
  }, [sid]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(`${API}/tax-chat`, { session_id: sid, message: msg });
      setMessages((m) => [...m, { role: 'assistant', text: res.data.reply }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assistant unavailable. Please try again.');
      setMessages((m) => m.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh', display: 'flex', flexDirection: 'column' }} data-testid="tax-assistant-page">
      <Navbar />
      <section style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', padding: '2.5rem 1.5rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <button onClick={() => navigate('/tax-services')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#10B981', cursor: 'pointer', marginBottom: '1rem', fontWeight: 600 }} data-testid="back-to-tax">
            <ArrowLeft size={18} /> Back to Tax Services
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(16,185,129,0.35)' }}>
              <Sparkles size={26} style={{ color: 'white' }} />
            </div>
            <div>
              <h1 style={{ fontSize: 'clamp(1.7rem, 4vw, 2.5rem)', color: '#0F172A', margin: 0, fontWeight: 800 }}>AI Tax Assistant</h1>
              <p style={{ color: '#475569', margin: '0.25rem 0 0' }}>Get instant answers on ITR, GST, regime selection & more.</p>
            </div>
          </div>
        </div>
      </section>

      <section style={{ flex: 1, padding: '1.5rem', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 900, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div ref={scrollRef} style={{ flex: 1, minHeight: 400, maxHeight: '60vh', overflowY: 'auto', background: '#F8FAFC', borderRadius: '1rem', padding: '1.25rem', border: '1px solid #E2E8F0' }} data-testid="tax-chat-window">
            {messages.length === 0 ? (
              <div style={{ padding: '2rem 1rem' }}>
                <p style={{ color: '#475569', textAlign: 'center', marginBottom: '1.5rem' }}>Hi! I'm your AI tax assistant. Try one of these:</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem' }}>
                  {STARTER_QUESTIONS.map((q) => (
                    <button key={q} onClick={() => send(q)} style={{ background: 'white', border: '1px solid #E2E8F0', color: '#475569', padding: '0.7rem 0.85rem', borderRadius: 10, cursor: 'pointer', fontSize: '0.85rem', textAlign: 'left', lineHeight: 1.4 }} data-testid="tax-starter-question">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: '1rem', flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }} data-testid={`tax-msg-${m.role}`}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: m.role === 'user' ? '#FF6B35' : 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {m.role === 'user' ? <UserIcon size={16} /> : <Bot size={16} />}
                  </div>
                  <div style={{ background: m.role === 'user' ? '#FF6B35' : 'white', color: m.role === 'user' ? 'white' : '#0F172A', padding: '0.7rem 0.95rem', borderRadius: 14, maxWidth: '78%', fontSize: '0.95rem', lineHeight: 1.55, whiteSpace: 'pre-wrap', border: m.role === 'user' ? 'none' : '1px solid #E2E8F0' }}>
                    {m.text}
                  </div>
                </div>
              ))
            )}
            {sending && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', color: '#64748B', fontSize: '0.85rem' }} data-testid="tax-typing">
                <Loader2 size={14} className="animate-spin" /> Tax assistant is thinking...
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              placeholder="Ask anything about ITR, GST, tax savings..."
              className="input-field"
              style={{ flex: 1 }}
              data-testid="tax-chat-input"
              disabled={sending}
            />
            <button onClick={() => send()} disabled={sending || !input.trim()} className="primary-button" style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', padding: '0.7rem 1rem' }} data-testid="tax-chat-send">
              <Send size={18} />
            </button>
          </div>
          <p style={{ color: '#94A3B8', fontSize: '0.75rem', textAlign: 'center', margin: 0 }}>
            Need a CA to file for you? <span style={{ color: '#10B981', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/tax-services')}>Browse services from ₹499</span>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default TaxAssistantPage;
